import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Search, 
  Filter, 
  MoreVertical, 
  Edit, 
  Copy, 
  Archive, 
  BarChart3,
  Users,
  Calendar,
  Eye
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface SurveyListProps {
  onEditSurvey: (survey: any) => void;
  canManage: boolean;
}

export function SurveyList({ onEditSurvey, canManage }: SurveyListProps) {
  const { user } = useAuth();
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    loadSurveys();
  }, [user]);

  const loadSurveys = async () => {
    try {
      // Get user's district_id first
      const { data: profile } = await supabase
        .from('profiles')
        .select('district_id')
        .eq('id', user?.id)
        .single();

      const { data, error } = await supabase
        .from('surveys')
        .select(`
          *,
          survey_responses(count)
        `)
        .eq('district_id', profile?.district_id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSurveys(data || []);
    } catch (error) {
      console.error('Error loading surveys:', error);
      toast.error('Failed to load surveys');
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicate = async (survey: any) => {
    try {
      const duplicatedSurvey = {
        ...survey,
        title: `${survey.title} (Copy)`,
        is_active: false,
        created_at: undefined,
        updated_at: undefined,
        id: undefined
      };

      const { error } = await supabase
        .from('surveys')
        .insert(duplicatedSurvey);

      if (error) throw error;

      toast.success('Survey duplicated successfully');
      loadSurveys();
    } catch (error) {
      console.error('Error duplicating survey:', error);
      toast.error('Failed to duplicate survey');
    }
  };

  const handleArchive = async (surveyId: string) => {
    try {
      const { error } = await supabase
        .from('surveys')
        .update({ active: false })
        .eq('id', surveyId);

      if (error) throw error;

      toast.success('Survey archived successfully');
      loadSurveys();
    } catch (error) {
      console.error('Error archiving survey:', error);
      toast.error('Failed to archive survey');
    }
  };

  const filteredSurveys = surveys.filter(survey => {
    const matchesSearch = survey.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         survey.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' && survey.is_active) ||
                         (statusFilter === 'inactive' && !survey.is_active);
    
    const matchesType = typeFilter === 'all' || survey.survey_type === typeFilter;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const getSurveyTypeLabel = (type: string) => {
    const types = {
      course_evaluation: 'Course Evaluation',
      instructor_feedback: 'Instructor Feedback',
      kpi_assessment: 'KPI Assessment',
      general_feedback: 'General Feedback',
      exit_survey: 'Exit Survey',
      satisfaction: 'Satisfaction'
    };
    return types[type] || type;
  };

  const getStatusBadge = (survey: any) => {
    if (!survey.active) {
      return <Badge variant="secondary">Inactive</Badge>;
    }
    
    const now = new Date();
    const endDate = survey.end_date ? new Date(survey.end_date) : null;
    
    if (endDate && now > endDate) {
      return <Badge variant="outline">Ended</Badge>;
    }
    
    return <Badge variant="default">Active</Badge>;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <Card key={i}>
            <CardContent className="p-6">
              <div className="animate-pulse space-y-3">
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-3 bg-muted rounded w-1/2"></div>
                <div className="h-3 bg-muted rounded w-1/4"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search surveys..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="course_evaluation">Course Evaluation</SelectItem>
            <SelectItem value="instructor_feedback">Instructor Feedback</SelectItem>
            <SelectItem value="kpi_assessment">KPI Assessment</SelectItem>
            <SelectItem value="general_feedback">General Feedback</SelectItem>
            <SelectItem value="exit_survey">Exit Survey</SelectItem>
            <SelectItem value="satisfaction">Satisfaction</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Survey List */}
      <div className="space-y-4">
        {filteredSurveys.map((survey) => (
          <Card key={survey.id} className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CardTitle className="text-lg">{survey.title}</CardTitle>
                  {getStatusBadge(survey)}
                  <Badge variant="outline">{getSurveyTypeLabel(survey.survey_type)}</Badge>
                </div>
                
                {canManage && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onEditSurvey(survey)}>
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDuplicate(survey)}>
                        <Copy className="h-4 w-4 mr-2" />
                        Duplicate
                      </DropdownMenuItem>
                      <DropdownMenuItem>
                        <BarChart3 className="h-4 w-4 mr-2" />
                        View Results
                      </DropdownMenuItem>
                      <DropdownMenuItem>
                        <Eye className="h-4 w-4 mr-2" />
                        Preview
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => handleArchive(survey.id)}
                        className="text-destructive"
                      >
                        <Archive className="h-4 w-4 mr-2" />
                        Archive
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </CardHeader>
            
            <CardContent>
              {survey.description && (
                <p className="text-muted-foreground mb-4">{survey.description}</p>
              )}
              
              <div className="flex items-center gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Users className="h-4 w-4" />
                  <span>{survey.survey_responses?.[0]?.count || 0} responses</span>
                </div>
                
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  <span>Created {new Date(survey.created_at).toLocaleDateString()}</span>
                </div>
                
                {survey.course_id && (
                  <div className="flex items-center gap-1">
                    <BarChart3 className="h-4 w-4" />
                    <span>Course-specific</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
        
        {filteredSurveys.length === 0 && (
          <Card>
            <CardContent className="py-8 text-center">
              <p className="text-muted-foreground">
                {searchTerm || statusFilter !== 'all' || typeFilter !== 'all' 
                  ? 'No surveys found matching your filters'
                  : 'No surveys created yet'
                }
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}